import React from 'react';
import { DropdownMenuActions, DropdownMenuActionType } from 'framework/ui/menu';
import {
  DOWNLOAD_COMPLIANCE_REGISTRY_DOCUMENTS,
  COMPLIANCE_DOCUMENT_STATUS,
  UPDATE_COMPLIANCE_REGISTRY_DOCUMENTS,
  ComplianceDocumentTypeFE,
} from 'domain/shared/compliance';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import ReplyAllIcon from '@mui/icons-material/ReplyAll';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import { useArtifactDownloadHelper } from 'domain/shared/artifact';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE, FormFieldsData } from 'framework/form';
import { useListContext } from 'framework/list';
import { useShowContext } from 'framework/show';
import ViewComplianceDocumentAction from './viewButton/ViewComplianceDocumentAction';
import EditComplianceDocumentAction from './EditComplianceDocumentAction';
import { useNavigate } from 'react-router-dom';
import { COMPLIANCE_MD_EDITOR_PATH } from 'domain/compliance/complianceRegistry/router';

interface ComplianceDocumentListActionsProps {
  isPrevious?: boolean;
}

const getPreviousDocumentPath = (entityData?: ComplianceDocumentTypeFE) => {
  const publishedVersion = [entityData, ...(entityData?.metadata?.previousVersions || [])].find(
    item => item?.metadata?.status === COMPLIANCE_DOCUMENT_STATUS.PUBLISHED,
  );

  return publishedVersion?.path || '';
};

const ComplianceDocumentListActions: React.FC<ComplianceDocumentListActionsProps> = ({ isPrevious = false }) => {
  const { addMessage } = useMessageService();
  const navigate = useNavigate();
  const { downloadDocumentStorage } = useArtifactDownloadHelper();
  const { reloadData, updateInList, deleteFromList } = useListContext();
  const { data } = useShowContext<ComplianceDocumentTypeFE>();

  const documentData = data.getData();
  const { metadata } = documentData;

  const handleDownload = async (filePath: string) => {
    try {
      await downloadDocumentStorage(filePath);
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const handleUpdateList = (updatedEntity: FormFieldsData) => {
    if (updatedEntity.metadata.status === 'DELETED') {
      deleteFromList!(updatedEntity.id || documentData.id);
      return;
    }

    updateInList!({
      ...documentData,
      path: updatedEntity.path || documentData.path,
      id: documentData.id,
      metadata: {
        ...metadata,
        entityId: updatedEntity.entityId,
        complianceName: updatedEntity.complianceName,
        complianceType: updatedEntity.complianceType,
        status: updatedEntity.status,
        isSignatureRequired: updatedEntity.isSignatureRequired,
        gracePeriodDays: updatedEntity.gracePeriodDays,
        isAllEmployee: updatedEntity.isAllEmployee,
        positionIds: updatedEntity.positionIds,
        offices: updatedEntity.offices,
        relatedEmployeeContractTypes: updatedEntity.relatedEmployeeContractTypes,
        assignments: updatedEntity.assignments,
      },
    } as any);
  };

  const reloadDataList = () => {
    reloadData!();
  };

  const createViewAction = (Icon: React.ComponentType, title: string, isVisible: boolean, permission: string) => ({
    isVisible,
    permission,
    form: {
      Component: ViewComplianceDocumentAction,
      title: 'Compliance document',
      afterSubmit: {
        execute: handleUpdateList,
      },
    },
    icon: <Icon />,
    title,
  });

  const getActions = (): DropdownMenuActionType[] => [
    createViewAction(
      PublishIcon,
      'Publish',
      metadata.status === COMPLIANCE_DOCUMENT_STATUS.UNPUBLISHED && !isPrevious,
      UPDATE_COMPLIANCE_REGISTRY_DOCUMENTS,
    ),
    createViewAction(
      ReplyAllIcon,
      'Unpublish',
      metadata.status === COMPLIANCE_DOCUMENT_STATUS.PUBLISHED && !isPrevious,
      UPDATE_COMPLIANCE_REGISTRY_DOCUMENTS,
    ),
    createViewAction(VisibilityIcon, 'View', !isPrevious, UPDATE_COMPLIANCE_REGISTRY_DOCUMENTS),
    {
      permission: UPDATE_COMPLIANCE_REGISTRY_DOCUMENTS,
      isVisible: !isPrevious,
      onClick: entityData => {
        const currentDocumentPath = entityData?.path || '';
        const previousDocumentPath = getPreviousDocumentPath(entityData);
        const params = new URLSearchParams({
          documentName: entityData?.metadata?.policyType || '',
          currentDocumentPath,
          previousDocumentPath,
          version: String(entityData?.metadata?.version || ''),
        });
        navigate(`${COMPLIANCE_MD_EDITOR_PATH}?${params.toString()}`);
      },
      icon: <EditIcon />,
      title: 'Edit document',
    },
    {
      permission: UPDATE_COMPLIANCE_REGISTRY_DOCUMENTS,
      isVisible: !isPrevious,
      form: {
        Component: EditComplianceDocumentAction,
        title: 'Edit compliance document',
        afterSubmit: {
          execute: reloadDataList,
        },
      },
      icon: <AutorenewIcon />,
      title: 'Change doc attributes',
    },
    {
      permission: DOWNLOAD_COMPLIANCE_REGISTRY_DOCUMENTS,
      onClick: entityData => handleDownload(entityData?.path),
      icon: <DownloadIcon />,
      title: 'Download',
    },
  ];

  return (
    <>
      <DropdownMenuActions actions={getActions()} />
    </>
  );
};

export default ComplianceDocumentListActions;
